/**
 * PURPOSE: Planificar partidos por fase y fecha con una interfaz compacta.
 * RESPONSIBILITIES: Mantener borradores por fase, organizar fechas en subtabs y guardar el lote validado.
 * COLLABORATORS: ParticipantSelect.jsx, FasesArea.jsx y phaseToolsService.js.
 * ROUTE: winscore/src/area/FasesArea/components/MatchPlannerModal.jsx
 */
import React, { useEffect, useMemo, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import Sheet from "@mui/joy/Sheet";
import {
  FiCalendar,
  FiCopy,
  FiPlus,
  FiSave,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import { toast } from "react-hot-toast";
import ParticipantSelect from "./ParticipantSelect.jsx";
import VenueSelect from "../../../components/Inputs/VenueSelect.jsx";
import { createPhaseMatches } from "../../../services/phaseToolsService.js";

const PHASE_GROUPS = 1;
const PHASE_FINALS = 2;
const makeKey = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const newRow = () => ({
  key: makeKey(),
  kind: "match",
  idparticipant1: "",
  idparticipant2: "",
  tag_final: "",
  date_match: "",
  time_match: "",
  idsede: "",
  scheduleOpen: false,
});

const newBlock = (nroFecha) => ({
  key: makeKey(),
  nroFecha,
  rows: [newRow()],
});

const createInitialDraft = () => {
  const groupBlock = newBlock(1);
  const finalBlock = newBlock(1);
  return {
    blocksByPhase: {
      [PHASE_GROUPS]: [groupBlock],
      [PHASE_FINALS]: [finalBlock],
    },
    activeByPhase: {
      [PHASE_GROUPS]: groupBlock.key,
      [PHASE_FINALS]: finalBlock.key,
    },
  };
};

const Field = ({ label, children, className = "" }) => (
  <label
    className={`grid min-w-0 gap-1.5 text-xs font-black text-slate-600 ${className}`}
  >
    {label}
    {children}
  </label>
);

const hasRowContent = (row) =>
  row.kind === "bye" ||
  Boolean(
    row.idparticipant1 ||
      row.idparticipant2 ||
      String(row.tag_final || "").trim() ||
      row.date_match ||
      row.time_match ||
      row.idsede
  );

const formatDate = (value) => {
  const [year, month, day] = String(value || "").split("-");
  return year && month && day ? `${day}/${month}/${year}` : "";
};

const getScheduleSummary = (row, venues) => {
  const venue = venues.find(
    (item) => Number(item.idsede) === Number(row.idsede)
  );
  const parts = [
    formatDate(row.date_match),
    row.time_match,
    venue?.name,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "Agregar programación";
};

const ScheduleFields = ({ row, venues, disabled, onPatch }) => (
  <div className="grid gap-3 rounded-xl border border-blue-100 bg-blue-50/50 p-3 sm:grid-cols-3">
    <Field label="Fecha">
      <input
        type="date"
        value={row.date_match}
        onChange={(event) => onPatch({ date_match: event.target.value })}
        disabled={disabled}
        className="min-h-[42px] rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none focus:border-blue-500"
      />
    </Field>
    <Field label="Hora">
      <input
        type="time"
        value={row.time_match}
        onChange={(event) => onPatch({ time_match: event.target.value })}
        disabled={disabled}
        className="min-h-[42px] rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none focus:border-blue-500"
      />
    </Field>
    <Field label="Sede">
      <VenueSelect
        venues={venues}
        value={row.idsede}
        onChange={(value) => onPatch({ idsede: value })}
        disabled={disabled}
        placeholder="Seleccionar sede"
      />
    </Field>
    <div className="flex flex-wrap gap-2 sm:col-span-3 sm:justify-end">
      <button
        type="button"
        onClick={() =>
          onPatch({
            date_match: "",
            time_match: "",
            idsede: "",
            scheduleOpen: false,
          })
        }
        disabled={disabled}
        className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-black text-slate-600 hover:bg-white disabled:opacity-50"
      >
        <FiX /> Limpiar
      </button>
      <button
        type="button"
        onClick={() => onPatch({ scheduleOpen: false })}
        disabled={disabled}
        className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-black text-blue-700 disabled:opacity-50"
      >
        Listo
      </button>
    </div>
  </div>
);

const MatchPlannerModal = ({
  open,
  onClose,
  idevent,
  idsport,
  context,
  onChanged,
}) => {
  const initialDraft = useMemo(createInitialDraft, []);
  const [phase, setPhase] = useState(PHASE_GROUPS);
  const [blocksByPhase, setBlocksByPhase] = useState(
    initialDraft.blocksByPhase
  );
  const [activeByPhase, setActiveByPhase] = useState(
    initialDraft.activeByPhase
  );
  const [saving, setSaving] = useState(false);
  const participants = context?.participants || [];
  const venues = context?.venues || [];
  const blocks = blocksByPhase[phase] || [];
  const activeKey = activeByPhase[phase];
  const activeBlock =
    blocks.find((block) => block.key === activeKey) || blocks[0];

  useEffect(() => {
    if (!open) return;
    const next = createInitialDraft();
    setPhase(PHASE_GROUPS);
    setBlocksByPhase(next.blocksByPhase);
    setActiveByPhase(next.activeByPhase);
  }, [open]);

  const plannedRows = useMemo(
    () =>
      Object.values(blocksByPhase).reduce(
        (total, phaseBlocks) =>
          total +
          phaseBlocks.reduce(
            (subtotal, block) =>
              subtotal + block.rows.filter(hasRowContent).length,
            0
          ),
        0
      ),
    [blocksByPhase]
  );

  const replacePhaseBlocks = (updater) => {
    setBlocksByPhase((current) => ({
      ...current,
      [phase]: updater(current[phase] || []),
    }));
  };

  const updateBlock = (blockKey, updater) => {
    replacePhaseBlocks((current) =>
      current.map((block) =>
        block.key === blockKey ? updater(block) : block
      )
    );
  };

  const patchRow = (blockKey, rowKey, patch) => {
    updateBlock(blockKey, (block) => ({
      ...block,
      rows: block.rows.map((row) =>
        row.key === rowKey
          ? {
              ...row,
              ...patch,
              ...(patch.kind === "bye" ? { idparticipant2: "" } : {}),
            }
          : row
      ),
    }));
  };

  const addRow = (blockKey) => {
    updateBlock(blockKey, (block) => ({
      ...block,
      rows: [...block.rows, newRow()],
    }));
  };

  const duplicateRow = (blockKey, row) => {
    updateBlock(blockKey, (block) => ({
      ...block,
      rows: [
        ...block.rows,
        { ...row, key: makeKey(), scheduleOpen: false },
      ],
    }));
  };

  const removeRow = (blockKey, rowKey) => {
    updateBlock(blockKey, (block) => ({
      ...block,
      rows: block.rows.filter((row) => row.key !== rowKey),
    }));
  };

  const addDate = () => {
    const maxDate = Math.max(
      0,
      ...blocks.map((block) => Number(block.nroFecha) || 0)
    );
    const next = newBlock(maxDate + 1);
    replacePhaseBlocks((current) => [...current, next]);
    setActiveByPhase((current) => ({ ...current, [phase]: next.key }));
  };

  const removeDate = (blockKey) => {
    const index = blocks.findIndex((block) => block.key === blockKey);
    let next = blocks.filter((block) => block.key !== blockKey);
    if (!next.length) next = [newBlock(1)];
    replacePhaseBlocks(() => next);
    setActiveByPhase((current) => ({
      ...current,
      [phase]: next[Math.max(0, Math.min(index, next.length - 1))].key,
    }));
  };

  const changePhase = (nextPhase) => {
    setPhase(Number(nextPhase));
  };

  const buildPayload = () =>
    [PHASE_GROUPS, PHASE_FINALS].flatMap((phaseId) =>
      (blocksByPhase[phaseId] || []).flatMap((block) =>
        block.rows.filter(hasRowContent).map((row) => ({
          idtypephase: phaseId,
          nro_fecha: Number(block.nroFecha),
          kind: row.kind,
          idparticipant1: row.idparticipant1
            ? Number(row.idparticipant1)
            : null,
          idparticipant2: row.idparticipant2
            ? Number(row.idparticipant2)
            : null,
          tag_final: row.tag_final,
          date_match: row.date_match || null,
          time_match: row.time_match || null,
          idsede: row.idsede ? Number(row.idsede) : null,
        }))
      )
    );

  const validate = (matches) => {
    if (!matches.length) {
      toast.error("Agrega al menos un partido.");
      return false;
    }

    for (const phaseId of [PHASE_GROUPS, PHASE_FINALS]) {
      const usedBlocks = (blocksByPhase[phaseId] || []).filter((block) =>
        block.rows.some(hasRowContent)
      );
      const dateNumbers = usedBlocks.map((block) => Number(block.nroFecha));
      const phaseLabel =
        phaseId === PHASE_GROUPS ? "fase de grupos" : "fase final";
      if (dateNumbers.some((number) => !Number.isInteger(number) || number <= 0)) {
        toast.error(`Revisa el número de fecha o etapa en ${phaseLabel}.`);
        return false;
      }
      if (new Set(dateNumbers).size !== dateNumbers.length) {
        toast.error(`No repitas fechas o etapas en ${phaseLabel}.`);
        return false;
      }
    }

    for (let index = 0; index < matches.length; index += 1) {
      const row = matches[index];
      const unitLabel =
        Number(row.idtypephase) === PHASE_GROUPS ? "Fecha" : "Etapa";
      const label = `${unitLabel} ${row.nro_fecha}, partido ${index + 1}`;

      if (row.kind === "bye") {
        const participant = participants.find(
          (item) =>
            Number(item.idparticipant) === Number(row.idparticipant1)
        );
        if (!participant) {
          toast.error(`${label}: selecciona quién descansa.`);
          return false;
        }
        if (!participant.idgrupo) {
          toast.error(`${label}: asigna un grupo al equipo que descansa.`);
          return false;
        }
        continue;
      }

      if (
        Number(row.idtypephase) === PHASE_GROUPS &&
        (!row.idparticipant1 || !row.idparticipant2)
      ) {
        toast.error(`${label}: selecciona ambos participantes.`);
        return false;
      }
      if (
        row.idparticipant1 &&
        row.idparticipant2 &&
        Number(row.idparticipant1) === Number(row.idparticipant2)
      ) {
        toast.error(`${label}: selecciona participantes diferentes.`);
        return false;
      }
      if (
        Number(row.idtypephase) === PHASE_FINALS &&
        !String(row.tag_final || "").trim()
      ) {
        toast.error(`${label}: ingresa la etiqueta del partido.`);
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    const matches = buildPayload();
    if (!validate(matches)) return;

    try {
      setSaving(true);
      const result = await createPhaseMatches({ idevent, idsport, matches });
      toast.success(`${result.createdMatches} partido(s) creado(s).`);
      await onChanged?.();
      onClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "No se pudo guardar el plan de partidos."
      );
    } finally {
      setSaving(false);
    }
  };

  const phaseUnit = phase === PHASE_GROUPS ? "Fecha" : "Etapa";

  const renderParticipantOne = (block, row) => (
    <ParticipantSelect
      value={row.idparticipant1}
      onChange={(value) =>
        patchRow(block.key, row.key, { idparticipant1: value })
      }
      options={participants}
      allowUndefined={phase === PHASE_FINALS}
      excludedValues={row.kind === "bye" ? [] : [row.idparticipant2]}
      disabled={saving}
      placeholder={
        row.kind === "bye" ? "Equipo que descansa" : "Participante 1"
      }
    />
  );

  const renderParticipantTwo = (block, row) => (
    <ParticipantSelect
      value={row.idparticipant2}
      onChange={(value) =>
        patchRow(block.key, row.key, { idparticipant2: value })
      }
      options={participants}
      allowUndefined={phase === PHASE_FINALS}
      excludedValues={[row.idparticipant1]}
      disabled={saving}
      placeholder="Participante 2"
    />
  );

  return (
    <Modal open={open} onClose={saving ? undefined : onClose} sx={{ p: 1.5 }}>
      <Sheet className="mx-auto flex max-h-[94vh] w-[min(98vw,1380px)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="relative border-b border-slate-200 px-5 py-4 pr-14">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600">
            Fases
          </p>
          <h2 className="mt-1 text-xl font-black text-slate-900">
            Planificar partidos
          </h2>
          <ModalClose disabled={saving} />
        </div>

        <div className="border-b border-slate-200 bg-slate-50 px-4 pt-3 sm:px-5">
          <div className="flex flex-col gap-3 pb-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="inline-flex w-fit rounded-xl border border-slate-200 bg-white p-1">
              {[
                { value: PHASE_GROUPS, label: "Fase de grupos" },
                { value: PHASE_FINALS, label: "Fase final" },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => changePhase(option.value)}
                  disabled={saving}
                  className={`rounded-lg px-4 py-2 text-sm font-black transition ${
                    phase === option.value
                      ? "bg-blue-600 text-white shadow"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-black text-slate-600 ring-1 ring-slate-200">
              {plannedRows} partido(s)
            </span>
          </div>

          <div className="flex items-end gap-2 overflow-x-auto pb-0">
            {blocks.map((block) => (
              <button
                key={block.key}
                type="button"
                onClick={() =>
                  setActiveByPhase((current) => ({
                    ...current,
                    [phase]: block.key,
                  }))
                }
                disabled={saving}
                className={`shrink-0 rounded-t-xl border border-b-0 px-4 py-2.5 text-sm font-black transition ${
                  activeBlock?.key === block.key
                    ? "border-blue-200 bg-white text-blue-700"
                    : "border-transparent bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
              >
                {phaseUnit} {block.nroFecha || "-"}
                <span className="ml-2 text-[10px] opacity-70">
                  {block.rows.filter(hasRowContent).length}
                </span>
              </button>
            ))}
            <button
              type="button"
              onClick={addDate}
              disabled={saving}
              title={`Añadir ${phaseUnit.toLowerCase()}`}
              className="mb-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
            >
              <FiPlus />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-slate-50/60 p-3 sm:p-5">
          {activeBlock ? (
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-end sm:justify-between">
                <Field label={`Número de ${phaseUnit.toLowerCase()}`}>
                  <input
                    type="number"
                    min="1"
                    value={activeBlock.nroFecha}
                    onChange={(event) =>
                      updateBlock(activeBlock.key, (current) => ({
                        ...current,
                        nroFecha: event.target.value,
                      }))
                    }
                    disabled={saving}
                    className="w-28 rounded-lg border border-slate-300 px-3 py-2 text-sm font-bold outline-none focus:border-blue-500"
                  />
                </Field>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => addRow(activeBlock.key)}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-xs font-black text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    <FiPlus /> Añadir partido
                  </button>
                  {blocks.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => removeDate(activeBlock.key)}
                      disabled={saving}
                      className="inline-flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs font-black text-red-700 hover:bg-red-100 disabled:opacity-50"
                    >
                      <FiTrash2 /> Eliminar {phaseUnit.toLowerCase()}
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="hidden xl:block">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1180px] table-fixed border-collapse">
                    <colgroup>
                      <col className="w-[48px]" />
                      <col className="w-[105px]" />
                      <col className="w-[245px]" />
                      <col className="w-[245px]" />
                      <col className="w-[220px]" />
                      <col className="w-[230px]" />
                      <col className="w-[90px]" />
                    </colgroup>
                    <thead className="bg-slate-50 text-left text-[11px] font-black uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-3">#</th>
                        <th className="px-2 py-3">Tipo</th>
                        <th className="px-2 py-3">Participante 1</th>
                        <th className="px-2 py-3">Participante 2</th>
                        <th className="px-2 py-3">Etiqueta</th>
                        <th className="px-2 py-3">Programación</th>
                        <th className="px-2 py-3 text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeBlock.rows.map((row, rowIndex) => (
                        <React.Fragment key={row.key}>
                          <tr className="border-t border-slate-100 align-middle">
                            <td className="px-3 py-3 text-sm font-black text-slate-500">
                              {rowIndex + 1}
                            </td>
                            <td className="px-2 py-3">
                              {phase === PHASE_GROUPS ? (
                                <select
                                  value={row.kind}
                                  onChange={(event) =>
                                    patchRow(activeBlock.key, row.key, {
                                      kind: event.target.value,
                                    })
                                  }
                                  disabled={saving}
                                  className="min-h-[46px] w-full rounded-xl border border-slate-300 bg-white px-2 text-sm font-bold outline-none focus:border-blue-500"
                                >
                                  <option value="match">Partido</option>
                                  <option value="bye">BYE</option>
                                </select>
                              ) : (
                                <span className="inline-flex min-h-[46px] w-full items-center rounded-xl bg-slate-100 px-3 text-sm font-bold text-slate-600">
                                  Partido
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-3">
                              {renderParticipantOne(activeBlock, row)}
                            </td>
                            <td className="px-2 py-3">
                              {row.kind === "bye" ? (
                                <span className="text-sm font-semibold text-slate-400">
                                  —
                                </span>
                              ) : (
                                renderParticipantTwo(activeBlock, row)
                              )}
                            </td>
                            <td className="px-2 py-3">
                              <input
                                value={row.tag_final}
                                onChange={(event) =>
                                  patchRow(activeBlock.key, row.key, {
                                    tag_final: event.target.value,
                                  })
                                }
                                maxLength={128}
                                disabled={saving}
                                placeholder={
                                  phase === PHASE_FINALS
                                    ? "Ej. Semifinal 1 *"
                                    : "Opcional"
                                }
                                className="min-h-[46px] w-full rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-blue-500"
                              />
                            </td>
                            <td className="px-2 py-3">
                              <button
                                type="button"
                                onClick={() =>
                                  patchRow(activeBlock.key, row.key, {
                                    scheduleOpen: !row.scheduleOpen,
                                  })
                                }
                                disabled={saving}
                                className={`flex min-h-[46px] w-full items-center gap-2 rounded-xl border px-3 text-left text-xs font-bold transition ${
                                  row.date_match || row.time_match || row.idsede
                                    ? "border-blue-200 bg-blue-50 text-blue-800"
                                    : "border-slate-300 bg-white text-slate-600 hover:border-blue-300"
                                }`}
                              >
                                <FiCalendar className="shrink-0" />
                                <span className="block truncate">
                                  {getScheduleSummary(row, venues)}
                                </span>
                              </button>
                            </td>
                            <td className="px-2 py-3">
                              <div className="flex justify-center gap-1">
                                <button
                                  type="button"
                                  onClick={() =>
                                    duplicateRow(activeBlock.key, row)
                                  }
                                  disabled={saving}
                                  title="Duplicar partido"
                                  className="rounded-lg p-2 text-slate-500 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-50"
                                >
                                  <FiCopy />
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    removeRow(activeBlock.key, row.key)
                                  }
                                  disabled={saving}
                                  title="Eliminar partido"
                                  className="rounded-lg p-2 text-slate-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                                >
                                  <FiTrash2 />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {row.scheduleOpen ? (
                            <tr className="border-t border-blue-100">
                              <td colSpan="7" className="px-3 py-3">
                                <ScheduleFields
                                  row={row}
                                  venues={venues}
                                  disabled={saving}
                                  onPatch={(patch) =>
                                    patchRow(activeBlock.key, row.key, patch)
                                  }
                                />
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!activeBlock.rows.length ? (
                  <div className="border-t border-slate-100 px-4 py-10 text-center text-sm font-semibold text-slate-500">
                    Añade un partido para comenzar.
                  </div>
                ) : null}
              </div>

              <div className="space-y-3 p-3 xl:hidden">
                {activeBlock.rows.map((row, rowIndex) => (
                  <article
                    key={row.key}
                    className="rounded-xl border border-slate-200 bg-white p-3"
                  >
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-black text-slate-600">
                        Partido {rowIndex + 1}
                      </span>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => duplicateRow(activeBlock.key, row)}
                          disabled={saving}
                          className="rounded-lg p-2 text-slate-500 hover:bg-blue-50 hover:text-blue-700"
                          title="Duplicar partido"
                        >
                          <FiCopy />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeRow(activeBlock.key, row.key)}
                          disabled={saving}
                          className="rounded-lg p-2 text-slate-500 hover:bg-red-50 hover:text-red-700"
                          title="Eliminar partido"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>

                    <div className="grid gap-3">
                      {phase === PHASE_GROUPS ? (
                        <Field label="Tipo">
                          <select
                            value={row.kind}
                            onChange={(event) =>
                              patchRow(activeBlock.key, row.key, {
                                kind: event.target.value,
                              })
                            }
                            disabled={saving}
                            className="min-h-[46px] rounded-xl border border-slate-300 bg-white px-3 text-sm font-bold outline-none focus:border-blue-500"
                          >
                            <option value="match">Partido</option>
                            <option value="bye">BYE</option>
                          </select>
                        </Field>
                      ) : null}

                      <Field
                        label={
                          row.kind === "bye"
                            ? "Equipo que descansa"
                            : "Participante 1"
                        }
                      >
                        {renderParticipantOne(activeBlock, row)}
                      </Field>
                      {row.kind !== "bye" ? (
                        <Field label="Participante 2">
                          {renderParticipantTwo(activeBlock, row)}
                        </Field>
                      ) : null}
                      <Field
                        label={
                          phase === PHASE_FINALS
                            ? "Etiqueta del partido *"
                            : "Etiqueta del partido"
                        }
                      >
                        <input
                          value={row.tag_final}
                          onChange={(event) =>
                            patchRow(activeBlock.key, row.key, {
                              tag_final: event.target.value,
                            })
                          }
                          maxLength={128}
                          disabled={saving}
                          placeholder={
                            phase === PHASE_FINALS
                              ? "Ej. Semifinal 1"
                              : "Opcional"
                          }
                          className="min-h-[46px] rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-blue-500"
                        />
                      </Field>
                      <button
                        type="button"
                        onClick={() =>
                          patchRow(activeBlock.key, row.key, {
                            scheduleOpen: !row.scheduleOpen,
                          })
                        }
                        disabled={saving}
                        className="flex min-h-[46px] items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 text-left text-sm font-bold text-slate-600"
                      >
                        <FiCalendar />
                        {getScheduleSummary(row, venues)}
                      </button>
                      {row.scheduleOpen ? (
                        <ScheduleFields
                          row={row}
                          venues={venues}
                          disabled={saving}
                          onPatch={(patch) =>
                            patchRow(activeBlock.key, row.key, patch)
                          }
                        />
                      ) : null}
                    </div>
                  </article>
                ))}
                {!activeBlock.rows.length ? (
                  <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm font-semibold text-slate-500">
                    Añade un partido para comenzar.
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-white px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-xl border border-slate-300 px-5 py-2.5 font-bold text-slate-700 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !plannedRows}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-2.5 font-black text-white shadow-lg shadow-blue-500/20 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FiSave /> {saving ? "Guardando..." : "Crear partidos"}
          </button>
        </div>
      </Sheet>
    </Modal>
  );
};

export default MatchPlannerModal;
